<template>
  <v-card class="add-booking">
    <v-card-title>{{ $t('admin_acc_booking_add.title') }}</v-card-title>
    <v-card-text>
      <v-col cols="4" class="overflow-y-auto pt-2">
        <v-form v-model="isFormValid"
                ref="addBookingForm">
          <v-select outlined
                    :label="$t('admin_acc_facility_type.title')"
                    :items="itemTypeOptions"
                    v-model="selectedItemTypeId"
                    :rules="requiredRules"></v-select>
          <v-select outlined
                    :label="$t('admin_acc_item.title')"
                    :items="itemOptions"
                    v-model="selectedItemId"></v-select>
          <v-select outlined
                    :label="$t('admin_acc_booking_add.type')"
                    :items="bookingTypeOptions"
                    v-model="selectedBookingType"
                    :rules="requiredRules"></v-select>
          <v-text-field v-if="selectedBookingType === 'maintenance_booking'"
                        outlined
                        :label="$t('admin_acc_booking.remarks')"
                        v-model="maintenanceRemarks"
                        :rules="requiredRules"></v-text-field>
          <v-autocomplete outlined
                          :label="$t('admin_acc_booking_add.user')"
                          :items="userOptions"
                          v-model="selectedUserId"
                          :rules="requiredRules"
                          v-if="selectedBookingType === 'user_booking'">
          </v-autocomplete>
          <v-checkbox v-if="selectedBookingType === 'user_booking'"
                      :label="$t('admin_acc_booking_add.is_recurring')"
                      v-model="isRecurring"></v-checkbox>

          <!--Single Booking-->
          <v-input v-if="!isRecurring"
                   :rules="requiredRules"
                   v-model="bookingStart">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_acc_booking_add.start_time') }}</legend>
              <DateTimePickerInput v-model="bookingStart"
                                   hide-details></DateTimePickerInput>
            </v-container>
          </v-input>
          <v-input v-if="!isRecurring"
                   :rules="requiredRules"
                   v-model="bookingEnd">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_acc_booking_add.end_time') }}</legend>
              <DateTimePickerInput v-model="bookingEnd"
                                   hide-details></DateTimePickerInput>
            </v-container>
          </v-input>

          <!--Recurring-->
          <v-text-field v-if="isRecurring"
                        outlined
                        :label="$t('admin_acc_booking_add.recur_title')"
                        :rules="requiredRules"
                        v-model="recurrenceTitle"></v-text-field>
          <v-input v-if="isRecurring"
                   :rules="requiredRules"
                   v-model="recurStartDate">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_acc_booking_add.recur_start_date') }}</legend>
              <DatePickerInput v-model="recurStartDate"
                               hide-details></DatePickerInput>
            </v-container>
          </v-input>
          <v-input v-if="isRecurring"
                   :rules="requiredRules"
                   v-model="recurEndDate">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_acc_booking_add.recur_end_date') }}</legend>
              <DatePickerInput v-model="recurEndDate"
                               hide-details></DatePickerInput>
            </v-container>
          </v-input>
          <v-input v-if="isRecurring"
                   v-model="recurrence"
                   :rules="requiredRules">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_acc_booking_add.repeat_every') }}</legend>
              <CronInput v-model="recurrence"
                         hide-details></CronInput>
            </v-container>
          </v-input>
          <v-input v-if="isRecurring"
                   :rules="requiredRules"
                   v-model="recurStartTime">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_acc_booking_add.recur_start_time') }}</legend>
              <TimePickerInput v-model="recurStartTime"
                               hide-details></TimePickerInput>
            </v-container>
          </v-input>
          <v-input v-if="isRecurring"
                   :rules="requiredRules"
                   v-model="recurEndTime">
            <v-container fluid class="flex-column pa-0">
              <legend class="v-label theme--light">{{ $t('admin_acc_booking_add.recur_end_time') }}</legend>
              <TimePickerInput v-model="recurEndTime"
                               hide-details></TimePickerInput>
            </v-container>
          </v-input>
        </v-form>
      </v-col>
      <v-col cols="8" class="overflow-y-auto">
        <AdminBookingList full-height
                          hide-search
                          hide-check-in
                          hide-edit
                          :extra-filters="{
                                status: [
                                    BOOKING_STATUS.CONFIRMED,
                                    BOOKING_STATUS.CREATED_BY_ADMIN,
                                    ],
                              }"
                          :extra-params="extraParams"
                          v-if="!isRecurring && bookingStart && bookingEnd"></AdminBookingList>
        <AdminRecurringBookingPreview :item-id="selectedItemId"
                                      :recurrence="recurrence"
                                      :start-date="recurStartDate"
                                      :end-date="recurEndDate"
                                      :start-time="recurStartTime"
                                      :end-time="recurEndTime"
                                      v-model="recurResults"
                                      v-if="isRecurring && selectedItemId && recurStartDate && recurEndDate&& recurStartTime && recurEndTime"></AdminRecurringBookingPreview>
      </v-col>
    </v-card-text>
    <v-card-actions>
      <v-btn @click="cancel"
             class="mr-3"
             depressed
             large>
        {{ $t('cancel') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary"
             depressed
             large
             @click="$refs.addBookingForm.validate() && save()">
        {{ $t('save') }}
        <v-icon right>fas fa-check</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import {DateTime} from 'luxon';
import {computed, onMounted, ref, watch} from "@vue/composition-api";
import DateTimePickerInput from "@/components/DateTimePickerInput";
import AdminBookingList from "@/components/acc_booking/booking/AdminBookingList";
import {BOOKING_STATUS} from "@/constants";
import CronInput from "@/components/CronInput.vue";
import DatePickerInput from "@/components/DatePickerInput.vue";
import TimePickerInput from "@/components/TimePickerInput.vue";
import AdminRecurringBookingPreview from "@/components/acc_booking/booking/AdminRecurringBookingPreview.vue";

export default {
  name: 'AddBookingModal',
  components: {AdminRecurringBookingPreview, TimePickerInput, DatePickerInput, CronInput, AdminBookingList, DateTimePickerInput},
  props: {
    itemTypeId: String,
    itemId: String,
    startTime: String,
    endTime: String,
  },
  setup(props, {root, emit}) {
    // Init
    onMounted(() => {
      getItemTypes();
      getUsers();
    })
    const cancel = function () {
      emit('submit');
    };

    // Form
    const isFormValid = ref(false);
    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);

    const itemTypes = ref([]);
    const itemTypeOptions = computed(() => {
      return itemTypes.value.map((t) => {
        return {text: t.name, value: t.id};
      });
    });
    const getItemTypes = function () {
      root.$callAPI('room/item_type/', {}, function (response) {
        itemTypes.value = response.body.item_types;
      });
    };
    const selectedItemTypeId = ref(props.itemTypeId);

    const items = ref([]);
    const itemOptions = computed(() => {
      return items.value.map((i) => {
        return {text: i.name, value: i.id};
      })
    })
    const getItems = function () {
      if (selectedItemTypeId.value) {
        root.$callAPI('room/item/', {type_id: selectedItemTypeId.value}, (response) => {
          items.value = response.body.items;
        });
      }
    }
    const selectedItemId = ref(props.itemId);
    watch(() => selectedItemTypeId.value, (newValue) => {
      if (newValue) {
        if (newValue !== props.itemTypeId) {
          selectedItemId.value = null;
        }
        getItems();
      }
    }, {immediate: true})

    const bookingTypeOptions = ref([
      {text: root.$t('admin_acc_booking_add.user_booking'), value: 'user_booking'},
      {text: root.$t('admin_acc_booking_add.maintenance_booking'), value: 'maintenance_booking'},
    ]);
    const selectedBookingType = ref(null);

    //
    const maintenanceRemarks = ref(null);

    // User Bookings
    const users = ref([]);
    const userOptions = computed(() => {
      return users.value.map((u) => {
        return {text: `${u.id.toString().padStart(6, '0')} - ${root.$t('name', {given_name: u.given_name, surname: u.surname})}`, value: u.account};
      })
    });
    const getUsers = function () {
      root.$callAPI('hkiarace/admin/user/simple/', {page_size: -1}, function (response) {
        users.value = response.body.users;
      });
    };
    const selectedUserId = ref(null);

    // Recurring
    const isRecurring = ref(false);
    const recurrenceTitle = ref(null);
    const recurStartDate = ref(null);
    const recurEndDate = ref(null);
    const recurStartTime = ref(null);
    const recurEndTime = ref(null);
    const recurrence = ref(null);
    const recurResults = ref(null);

    // Single Booking
    const bookingStart = ref(props.startTime);
    const bookingEnd = ref(props.endTime);

    const feeRules = ref([
      value => (waiveFee.value || !!value) || root.$t('register.required_fields'),
    ])
    const waiveFee = ref(true);
    const bookingFee = ref(0);


    const extraParams = computed(() => {
      if (!selectedItemId.value || !bookingStart.value || !bookingEnd.value) return {};

      return {
        start_time: DateTime.fromISO(bookingStart.value),
        end_time: DateTime.fromISO(bookingEnd.value),
        item_id: selectedItemId.value,
        with_exclusions: true,
      }
    })

    const save = function () {
      if (!isRecurring.value) {
        if (confirm(root.$t('admin_acc_booking_add.refund_all_warning'))) {
          let url = selectedBookingType.value === 'user_booking' ? 'aafb/admin/booking/create_admin_booking/' : 'aafb/admin/booking/create_maintenance_booking/';

          root.$callAPI(url, {
            item_id: selectedItemId.value,
            user_id: selectedUserId.value,
            start_time: bookingStart.value,
            end_time: bookingEnd.value,
            waive_fee: waiveFee.value,
            fee: waiveFee.value ? 0 : bookingFee.value,
            remarks: maintenanceRemarks.value,
          }, (response) => {
            emit('submit', response.body.booking);
          });
        }
      } else {
        if (recurResults.value) {
          if (confirm(root.$t('admin_acc_booking_add.refund_all_warning'))) {
            root.$callAPI('aafb/admin/booking/create_recurring_booking/', {
              user_id: selectedUserId.value,
              title: recurrenceTitle.value,
              bookings: recurResults.value.bookings,
            }, (response) => {
              emit('submit', response.body.booking);
            });
          }
        } else {
          alert('Clear all pending bookings')
        }
      }
    }

    return {
      cancel,

      isFormValid,
      requiredRules,

      itemTypeOptions,
      selectedItemTypeId,

      itemOptions,
      selectedItemId,

      isRecurring,
      recurrenceTitle,
      recurStartDate,
      recurEndDate,
      recurStartTime,
      recurEndTime,
      recurrence,
      recurResults,

      feeRules,
      waiveFee,
      bookingFee,

      bookingTypeOptions,
      selectedBookingType,

      maintenanceRemarks,

      bookingStart,
      bookingEnd,

      userOptions,
      selectedUserId,

      extraParams,

      save,

      BOOKING_STATUS,
    }
  },
}
</script>

<style lang="less">
.add-booking {
  .v-card__text {
    display: flex;
    height: 75vh;
    padding: 0;
  }
}
</style>
